/**
 * Form drawer
 * -----------
 * Opens a <drawer-content class="lh-form-drawer"> when a same-page link to its
 * id is clicked (e.g. <a href="#form-drawer">), and submits the wrapped Shopify
 * {% form 'contact' %} over AJAX so the drawer stays open and shows an inline
 * success / error banner.
 *
 * The drawer open/close, focus trap and body scroll-lock are provided entirely
 * by the theme's registered <drawer-content> custom element — this file only
 * adds the link interception and the AJAX submission.
 */
(function () {
  if (window.__lhFormDrawerLoaded) return;
  window.__lhFormDrawerLoaded = true;

  var SELECTOR = 'drawer-content.lh-form-drawer[data-form-drawer]';

  function escapeHtml(value) {
    var el = document.createElement('div');
    el.textContent = value == null ? '' : String(value);
    return el.innerHTML;
  }

  function readConfig(drawer) {
    return {
      successMessage:
        drawer.getAttribute('data-success-message') || 'Thanks! Your message has been sent.',
      errorMessage:
        drawer.getAttribute('data-error-message') ||
        'Something went wrong. Please try again.',
      autoClose: drawer.hasAttribute('data-auto-close'),
      autoCloseMs: parseInt(drawer.getAttribute('data-auto-close-ms'), 10) || 3000,
      // Blank endpoint keeps the original Shopify contact-form submission.
      endpoint: drawer.getAttribute('data-endpoint') || '',
      branch: drawer.getAttribute('data-branch') || '',
      fieldsKey: drawer.getAttribute('data-fields-key') || 'fields',
      includeContext: drawer.hasAttribute('data-include-context'),
      heading: drawer.getAttribute('data-heading') || '',
      turnstileKey: drawer.getAttribute('data-turnstile-key') || ''
    };
  }

  function setBusy(form, busy) {
    var btn = form.querySelector('[data-submit]');
    if (!btn) return;
    // CSS keys the spinner/label swap off aria-busy, so there's no layout shift.
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    btn.disabled = busy;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Cross-fade from the form to the success panel.
  function showSuccess(drawer) {
    var wrap = drawer.querySelector('[data-form-wrap]');
    var panel = drawer.querySelector('[data-success-panel]');
    if (!panel) return;
    function reveal() {
      if (wrap) { wrap.hidden = true; wrap.style.opacity = ''; }
      panel.hidden = false;
      var btn = panel.querySelector('[data-action="close"]');
      if (!prefersReducedMotion()) {
        panel.style.opacity = '0';
        panel.style.transform = 'translateY(10px)';
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            panel.style.opacity = '1';
            panel.style.transform = 'translateY(0)';
          });
        });
      }
      if (btn) { try { btn.focus(); } catch (e) {} }
    }
    if (wrap && !prefersReducedMotion()) {
      wrap.style.opacity = '0';
      window.setTimeout(reveal, 220);
    } else {
      reveal();
    }
  }

  // Restore the form view (used when the drawer is reopened after a success).
  function resetToForm(drawer) {
    var wrap = drawer.querySelector('[data-form-wrap]');
    var panel = drawer.querySelector('[data-success-panel]');
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    panel.style.opacity = '';
    panel.style.transform = '';
    if (wrap) { wrap.hidden = false; wrap.style.opacity = ''; }
  }

  function showBanner(form, type, message) {
    var banner = form.querySelector('[data-live-banner]');
    if (!banner) return;
    var isSuccess = type === 'success';
    banner.classList.remove('lh-form-drawer__banner--success', 'lh-form-drawer__banner--error');
    banner.classList.add(isSuccess ? 'lh-form-drawer__banner--success' : 'lh-form-drawer__banner--error');
    banner.setAttribute('role', isSuccess ? 'status' : 'alert');
    var icon = isSuccess
      ? '<span class="lh-form-drawer__banner-icon"><svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6.5 5 9 9.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>'
      : '<span class="lh-form-drawer__banner-icon" aria-hidden="true">!</span>';
    banner.innerHTML = icon + '<div class="lh-form-drawer__banner-text">' + escapeHtml(message) + '</div>';
    banner.hidden = false;
    if (typeof banner.scrollIntoView === 'function') {
      banner.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  function hideBanner(form) {
    var live = form.querySelector('[data-live-banner]');
    if (live) live.hidden = true;
    // Hide any server-rendered banners once the user re-submits via AJAX.
    var ssr = form.querySelectorAll('[data-banner]');
    for (var i = 0; i < ssr.length; i++) ssr[i].hidden = true;
  }

  // Hidden iframe the contact form posts into, so the page doesn't navigate.
  function ensureSink(drawer, form) {
    if (form.__lhSink && form.__lhSink.isConnected) return form.__lhSink;
    var name = 'lh-fd-sink-' + (drawer.id || 'form');
    var iframe = document.createElement('iframe');
    iframe.name = name;
    iframe.title = 'form submission target';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.tabIndex = -1;
    iframe.style.cssText = 'position:absolute;left:-9999px;width:0;height:0;border:0;';
    (drawer.querySelector('.lh-form-drawer__panel') || document.body).appendChild(iframe);
    form.__lhSink = iframe;
    form.setAttribute('target', name);
    return iframe;
  }

  function readError(doc, fallback) {
    try {
      var node = doc.querySelector(
        '.lh-form-drawer__banner--error .lh-form-drawer__banner-text, .banner--error .banner__content, .errors'
      );
      if (node) {
        var text = node.textContent.trim().replace(/\s+/g, ' ');
        if (text) return text;
      }
    } catch (e) {
      /* cross-origin or not present */
    }
    return fallback;
  }

  // Submit the real {% form 'contact' %} into a hidden iframe. This is a genuine
  // native submit, so Shopify's spam protection (reCAPTCHA / Turnstile) runs and
  // injects its token — an AJAX fetch would bypass the captcha and be rejected —
  // while the drawer stays open. Success/error is read from the iframe response.
  function onSubmit(event, drawer, form, cfg) {
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      event.preventDefault();
      if (typeof form.reportValidity === 'function') form.reportValidity();
      return;
    }

    var sink = ensureSink(drawer, form);
    form.target = sink.name;
    setBusy(form, true);
    hideBanner(form);

    var settled = false;
    var timer = 0;

    function settle(after) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      sink.removeEventListener('load', onLoad);
      setBusy(form, false);
      if (after) after();
    }

    function onLoad() {
      var href = '';
      var doc = null;
      try { href = sink.contentWindow.location.href; } catch (e) { href = ''; }
      try { doc = sink.contentDocument; } catch (e) { doc = null; }
      // The sink is created up front, so this load is the submission response.
      // Default to success and only report an error when we can positively read
      // a Shopify error in a same-origin response. A cross-origin redirect
      // (Shopify often redirects to the canonical domain on success) is
      // unreadable and means the submission was accepted.
      var success = true;
      var message = cfg.successMessage;
      if (!/[?&]contact_posted=true/.test(href) && doc) {
        // Our SSR error banner only renders when {% if form.errors %} is set,
        // so its presence (with text) is a reliable error signal. .banner--error
        // is the theme's equivalent. Require non-empty text to avoid matching an
        // unrelated empty error container on the page.
        var errEl = doc.querySelector('.lh-form-drawer__banner--error, .banner--error');
        if (errEl && errEl.textContent.replace(/\s+/g, ' ').trim()) {
          success = false;
          message = readError(doc, cfg.errorMessage);
        }
      }
      settle(function () {
        if (success) {
          form.reset();
          showSuccess(drawer);
          if (cfg.autoClose) {
            window.setTimeout(function () { drawer.open = false; }, cfg.autoCloseMs);
          }
        } else {
          showBanner(form, 'error', message);
        }
      });
    }

    timer = window.setTimeout(function () {
      settle(function () { showBanner(form, 'error', cfg.errorMessage); });
    }, 20000);

    sink.addEventListener('load', onLoad);
    // Do NOT preventDefault — let the native, captcha-aware submit proceed.
  }

  /* ------------------------------------------------------------------ */
  /* Lucky Honey backend submission                                      */
  /* ------------------------------------------------------------------ */

  // Categories as an object the backend can validate: { "Grip Socks": 500 }.
  // Unchecked categories are omitted rather than sent as 0. With the quantity
  // stepper switched off there are no numbers to send, so the group submits the
  // same comma-joined string as the multi-select chips field.
  function readCategoryValue(group) {
    var rows = group.querySelectorAll('[data-fd-category]');
    var withQty = !group.hasAttribute('data-fd-no-qty');
    var names = [];
    var out = {};
    for (var i = 0; i < rows.length; i++) {
      var check = rows[i].querySelector('[data-fd-category-check]');
      if (!check || !check.checked) continue;
      if (!withQty) {
        names.push(check.value);
        continue;
      }
      var qty = rows[i].querySelector('[data-fd-qty]');
      if (!qty) continue;
      var value = parseInt(qty.value, 10);
      if (!isNaN(value) && value > 0) out[check.value] = value;
    }
    return withQty ? out : names.join(', ');
  }

  // Build the JSON payload from data-lh-key, which is the contract with the
  // backend's branches.ts. Anything without a key is not sent; anything the
  // backend does not recognise is discarded there, silently, with a 200.
  function collectFields(form) {
    var nodes = form.querySelectorAll('[data-lh-key]');
    var fields = {};
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var key = el.getAttribute('data-lh-key');
      if (!key) continue;

      if (el.hasAttribute('data-fd-categories')) {
        fields[key] = readCategoryValue(el);
      } else if (el.hasAttribute('data-fd-chips')) {
        var joined = el.querySelector('[data-fd-chip-value]');
        fields[key] = joined ? joined.value : '';
      } else if (el.hasAttribute('data-fd-radio')) {
        var picked = el.querySelector('input[type="radio"]:checked');
        fields[key] = picked ? picked.value : '';
      } else if (el.type === 'checkbox') {
        fields[key] = el.checked ? el.value : '';
      } else {
        fields[key] = el.value == null ? '' : String(el.value);
      }
    }
    return fields;
  }

  function collectLabels(form) {
    var nodes = form.querySelectorAll('[data-lh-key]');
    var labels = {};
    for (var i = 0; i < nodes.length; i++) {
      var key = nodes[i].getAttribute('data-lh-key');
      if (key) labels[key] = nodes[i].getAttribute('data-lh-label') || key;
    }
    return labels;
  }

  var ERROR_MESSAGES = {
    turnstile_failed: 'We could not verify you are human. Please try again.',
    rate_limited: 'You have sent several requests already. Please wait a few minutes and try again.',
    unknown_branch: 'This form is misconfigured. Please contact us directly.',
    email_failed: 'We received your request but could not send your confirmation email.',
    invalid_json: 'Something went wrong sending your details. Please try again.',
    invalid_body: 'Something went wrong sending your details. Please try again.'
  };

  // The backend can name a field the form doesn't render — a backend-only field,
  // or a Field key that doesn't match branches.ts — so make the raw key readable.
  function humanizeKey(key) {
    return String(key).replace(/_/g, ' ').replace(/^./, function (char) {
      return char.toUpperCase();
    });
  }

  // Backend codes are `reason` or `reason:fieldKey`.
  function messageForError(code, labels, fallback) {
    if (!code) return fallback;
    var parts = String(code).split(':');
    var reason = parts[0];
    var label = parts[1] ? labels[parts[1]] || humanizeKey(parts[1]) : '';
    switch (reason) {
      case 'field_required':
        return label + ' is required.';
      case 'field_too_long':
        return label + ' is too long.';
      case 'field_invalid_option':
        return 'Please choose a valid option for ' + label + '.';
      case 'field_invalid_quantity':
        return 'Please check the quantities for ' + label + '.';
      case 'field_invalid_email':
        return 'Please enter a valid email address.';
      case 'invalid_field':
        return label + ' is not valid.';
      default:
        return ERROR_MESSAGES[reason] || fallback;
    }
  }

  function resetTurnstile(form) {
    if (!window.turnstile) return;
    var widget = form.querySelector('.cf-turnstile');
    try {
      if (widget) window.turnstile.reset(widget);
      else window.turnstile.reset();
    } catch (e) {
      /* widget not rendered yet */
    }
  }

  /**
   * Resolve the Endpoint URL setting to the URL to POST to. Accepts:
   *
   *   https://host                  → https://host/api/submit   (origin only)
   *   https://host/                 → https://host/api/submit
   *   https://host/api/submit       → used as-is
   *   https://host/anything/else    → used as-is (custom route)
   *   /apps/lucky-honey/submit      → used as-is (same-origin, e.g. a Shopify
   *                                   App Proxy, which sidesteps CORS entirely)
   */
  function resolveEndpoint(raw) {
    var value = String(raw || '').trim().replace(/\/+$/, '');
    if (!value) return '';
    if (value.charAt(0) === '/') return value;
    try {
      var parsed = new URL(value);
      if (parsed.pathname && parsed.pathname !== '/') return value;
    } catch (e) {
      // Not an absolute URL — treat it as an origin and let fetch complain.
    }
    return value + '/api/submit';
  }

  /**
   * Shape the request body:
   *
   *   { branch, <fieldsKey>: { key: value, … }, turnstileToken?, context? }
   *
   * Category answers stay objects so the backend can validate the names and
   * quantities rather than re-parsing a string. Returns { body, contentType }.
   */
  function buildBody(form, cfg) {
    var payload = {};
    if (cfg.branch) payload.branch = cfg.branch;
    payload[cfg.fieldsKey || 'fields'] = collectFields(form);

    var token = form.querySelector('[name="cf-turnstile-response"]');
    if (token && token.value) payload.turnstileToken = token.value;

    if (cfg.includeContext) {
      payload.context = {
        page_url: window.location.href,
        referrer: document.referrer || '',
        form_name: cfg.heading
      };
    }

    return { body: JSON.stringify(payload), contentType: 'application/json' };
  }

  // POST JSON to the Lucky Honey backend. Unlike the Shopify path this is a real
  // fetch, so Shopify's spam protection never runs — Turnstile is the only
  // equivalent, which is why the section nags for a site key.
  function submitToEndpoint(event, drawer, form, cfg) {
    event.preventDefault();

    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      if (typeof form.reportValidity === 'function') form.reportValidity();
      return;
    }

    syncAllChips(drawer);
    syncAllCategories(drawer);

    var labels = collectLabels(form);
    var request = buildBody(form, cfg);

    setBusy(form, true);
    hideBanner(form);

    var controller = null;
    var timer = 0;
    if (typeof AbortController === 'function') {
      controller = new AbortController();
      timer = window.setTimeout(function () { controller.abort(); }, 20000);
    }

    fetch(resolveEndpoint(cfg.endpoint), {
      method: 'POST',
      headers: { 'content-type': request.contentType },
      body: request.body,
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () { return {}; })
          .then(function (body) { return { ok: response.ok, body: body }; });
      })
      .then(function (result) {
        if (!result.ok) {
          throw new Error(messageForError(result.body.error, labels, cfg.errorMessage));
        }
        form.reset();
        resetTurnstile(form);
        showSuccess(drawer);
        if (cfg.autoClose) {
          window.setTimeout(function () { drawer.open = false; }, cfg.autoCloseMs);
        }
      })
      .catch(function (error) {
        // A Turnstile token is single-use, so a retry needs a fresh one.
        resetTurnstile(form);
        var message = error && error.name === 'AbortError' ? cfg.errorMessage : error.message;
        showBanner(form, 'error', message || cfg.errorMessage);
      })
      .then(function () {
        window.clearTimeout(timer);
        setBusy(form, false);
      });
  }

  // Multi-select chips: each group's checked chips are joined into one hidden
  // contact[Label] field (so the email shows a single "Label: A, B" line). The
  // chip checkboxes carry no name, so they never submit individually.
  function syncChipGroup(group) {
    var inputs = group.querySelectorAll('[data-fd-chip]');
    var hidden = group.querySelector('[data-fd-chip-value]');
    var values = [];
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].checked) values.push(inputs[i].value);
    }
    if (hidden) hidden.value = values.join(', ');
    // Hidden inputs are barred from constraint validation, so enforce "select at
    // least one" on the first (visible, focusable) chip checkbox instead.
    if (group.hasAttribute('data-fd-required') && inputs.length) {
      inputs[0].setCustomValidity(values.length ? '' : 'Please select at least one option.');
    }
  }

  function syncAllChips(drawer) {
    var groups = drawer.querySelectorAll('[data-fd-chips]');
    for (var i = 0; i < groups.length; i++) syncChipGroup(groups[i]);
  }

  // Category rows with a quantity stepper. Checked rows are joined into one
  // hidden field as "Socks: 300, Activewear: 200".
  function syncCategoryGroup(group) {
    var rows = group.querySelectorAll('[data-fd-category]');
    var hidden = group.querySelector('[data-fd-category-value]');
    var min = parseInt(group.getAttribute('data-fd-min'), 10) || 0;
    var step = parseInt(group.getAttribute('data-fd-step'), 10) || 1;
    var parts = [];
    var firstCheck = null;

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var check = row.querySelector('[data-fd-category-check]');
      var qty = row.querySelector('[data-fd-qty]');
      if (!check) continue;
      if (!firstCheck) firstCheck = check;

      row.classList.toggle('is-selected', check.checked);
      if (!check.checked) continue;

      // No stepper (quantity selection turned off) — the name is the whole value.
      if (!qty) {
        parts.push(check.value);
        continue;
      }

      // Clamp to the minimum and snap to the step, so a typed value can't slip
      // below the MOQ or land between increments.
      var value = parseInt(qty.value, 10);
      if (isNaN(value) || value < min) value = min;
      var offset = value - min;
      value = min + Math.round(offset / step) * step;
      qty.value = String(value);

      parts.push(check.value + ': ' + value);
    }

    if (hidden) hidden.value = parts.join(', ');

    // Same trick as the chips: hidden inputs are barred from constraint
    // validation, so "pick at least one" is enforced on the first checkbox.
    if (group.hasAttribute('data-fd-required') && firstCheck) {
      firstCheck.setCustomValidity(parts.length ? '' : 'Please select at least one category.');
    }
  }

  function syncAllCategories(drawer) {
    var groups = drawer.querySelectorAll('[data-fd-categories]');
    for (var i = 0; i < groups.length; i++) syncCategoryGroup(groups[i]);
  }

  // A link to the drawer can name categories to tick, inside the fragment:
  // #form-drawer?category=Socks (comma-separate for several). Keeping the whole
  // thing in the hash means the trigger stays a plain link and the page URL
  // never picks up a query string.
  function parseTarget(hash) {
    var raw = hash.charAt(0) === '#' ? hash.slice(1) : hash;
    if (!raw) return null;

    var mark = raw.indexOf('?');
    var id = mark === -1 ? raw : raw.slice(0, mark);
    var query = mark === -1 ? '' : raw.slice(mark + 1);
    if (!id) return null;

    var categories = null;
    try {
      categories = new URLSearchParams(query).get('category');
    } catch (e) {
      /* no URLSearchParams or malformed query — just open the drawer */
    }

    return { id: decodeURIComponent(id), categories: categories };
  }

  // Tick the named categories on open. Additive: whatever the visitor already
  // chose stays chosen. Names are matched loosely so a product card's label
  // doesn't have to match the drawer's casing exactly.
  function preselectCategories(drawer, names) {
    if (!names) return;
    var wanted = names.split(',').map(function (name) {
      return name.trim().toLowerCase();
    }).filter(Boolean);
    if (!wanted.length) return;

    var groups = drawer.querySelectorAll('[data-fd-categories]');
    var first = null;

    for (var i = 0; i < groups.length; i++) {
      var inputs = groups[i].querySelectorAll('[data-fd-category-check]');
      var matched = false;
      for (var j = 0; j < inputs.length; j++) {
        if (wanted.indexOf(inputs[j].value.trim().toLowerCase()) === -1) continue;
        inputs[j].checked = true;
        matched = true;
        if (!first) first = inputs[j];
      }
      if (matched) syncCategoryGroup(groups[i]);
    }

    // The category list can sit well below the fold, so bring the selection
    // into view — otherwise the preselection is invisible on a phone.
    if (first && typeof first.closest === 'function') {
      var row = first.closest('[data-fd-category]');
      if (row && typeof row.scrollIntoView === 'function') {
        row.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  function initCategories(drawer) {
    var groups = drawer.querySelectorAll('[data-fd-categories]');
    for (var i = 0; i < groups.length; i++) {
      (function (group) {
        group.addEventListener('change', function (event) {
          if (
            event.target &&
            event.target.matches('[data-fd-category-check], [data-fd-qty]')
          ) {
            syncCategoryGroup(group);
          }
        });

        group.addEventListener('click', function (event) {
          var button = event.target.closest && event.target.closest('[data-fd-qty-step]');
          if (!button || !group.contains(button)) return;
          var row = button.closest('[data-fd-category]');
          var qty = row && row.querySelector('[data-fd-qty]');
          if (!qty) return;

          var min = parseInt(group.getAttribute('data-fd-min'), 10) || 0;
          var step = parseInt(group.getAttribute('data-fd-step'), 10) || 1;
          var direction = parseInt(button.getAttribute('data-fd-qty-step'), 10) || 0;
          var value = parseInt(qty.value, 10);
          if (isNaN(value)) value = min;

          qty.value = String(Math.max(min, value + direction * step));
          syncCategoryGroup(group);
        });
      })(groups[i]);
    }
    syncAllCategories(drawer);
  }

  function initChips(drawer) {
    var groups = drawer.querySelectorAll('[data-fd-chips]');
    for (var i = 0; i < groups.length; i++) {
      (function (group) {
        group.addEventListener('change', function (event) {
          if (event.target && event.target.matches('[data-fd-chip]')) syncChipGroup(group);
        });
      })(groups[i]);
    }
    syncAllChips(drawer);
  }

  // Swipe-down-to-dismiss for the mobile bottom sheet.
  function initSheetDrag(drawer) {
    var panel = drawer.querySelector('.lh-form-drawer__panel');
    if (!panel) return;
    var overlay = drawer.querySelector('.lh-form-drawer__overlay');
    var handles = [
      drawer.querySelector('[data-fd-grabber]'),
      drawer.querySelector('.lh-form-drawer__header')
    ];
    var mq = window.matchMedia('(max-width: 740px)');
    var startY = 0;
    var dy = 0;
    var height = 1;
    var dragging = false;

    function onMove(e) {
      if (!dragging) return;
      dy = Math.max(0, e.clientY - startY);
      panel.style.transform = 'translateY(' + dy + 'px)';
      if (overlay) overlay.style.opacity = String(Math.max(0, 1 - dy / height));
      if (e.cancelable) e.preventDefault();
    }

    function finishClose() {
      panel.removeEventListener('transitionend', finishClose);
      drawer.open = false;
      panel.style.transform = '';
      if (overlay) overlay.style.opacity = '';
    }

    function onUp() {
      if (!dragging) return;
      dragging = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      panel.classList.remove('is-dragging');
      if (dy > Math.min(140, height * 0.28)) {
        panel.style.transform = 'translateY(100%)';
        panel.addEventListener('transitionend', finishClose);
        window.setTimeout(finishClose, 400);
      } else {
        panel.style.transform = '';
        if (overlay) overlay.style.opacity = '';
      }
    }

    function onDown(e) {
      if (!mq.matches || dragging) return;
      if (e.target.closest && e.target.closest('button, a, input, select, textarea')) return;
      dragging = true;
      dy = 0;
      startY = e.clientY;
      height = panel.getBoundingClientRect().height || 1;
      panel.classList.add('is-dragging');
      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', onUp, { once: true });
      window.addEventListener('pointercancel', onUp, { once: true });
    }

    for (var i = 0; i < handles.length; i++) {
      if (handles[i]) handles[i].addEventListener('pointerdown', onDown);
    }
  }

  function initDrawer(drawer) {
    if (drawer.__lhFdInit) return;
    drawer.__lhFdInit = true;
    initSheetDrag(drawer);
    initChips(drawer);
    initCategories(drawer);
    var form = drawer.querySelector('form');
    if (!form) return;
    var cfg = readConfig(drawer);
    // The iframe sink is only for the Shopify path; the endpoint path fetches.
    if (!cfg.endpoint) {
      ensureSink(drawer, form); // create the submit target up front (no about:blank race)
    }
    form.addEventListener('submit', function (event) {
      if (cfg.endpoint) submitToEndpoint(event, drawer, form, cfg);
      else onSubmit(event, drawer, form, cfg);
    });
    // form.reset() (after a success) clears the chips; re-sync once the native
    // reset has applied so the hidden value and required state track again.
    form.addEventListener('reset', function () {
      window.requestAnimationFrame(function () {
        syncAllChips(drawer);
        syncAllCategories(drawer);
      });
    });
    // Reopening after a success returns to a fresh form.
    drawer.addEventListener('openable-element:open', function () {
      resetToForm(drawer);
    });
  }

  function initAll() {
    var drawers = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < drawers.length; i++) initDrawer(drawers[i]);
  }

  // Landing on /pages/x#form-drawer?category=Socks opens the drawer the same way
  // clicking such a link does, so the URL is shareable rather than click-only.
  function openFromLocation() {
    var target = parseTarget(window.location.hash);
    if (!target) return;
    var drawer = document.getElementById(target.id);
    if (!drawer || !drawer.matches(SELECTOR)) return;
    initDrawer(drawer);
    drawer.open = true;
    preselectCategories(drawer, target.categories);
  }

  // Open the matching drawer when a same-page link to its id is clicked.
  document.addEventListener('click', function (event) {
    var anchor = event.target.closest && event.target.closest('a[href*="#"]');
    if (!anchor) return;
    var url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (e) {
      return;
    }
    if (url.host !== window.location.host || url.pathname !== window.location.pathname) return;
    var target = parseTarget(url.hash);
    if (!target) return;
    var drawer = document.getElementById(target.id);
    if (!drawer || !drawer.matches(SELECTOR)) return;
    event.preventDefault();
    if (drawer.open !== true) drawer.open = true;
    preselectCategories(drawer, target.categories);
  });

  function boot() {
    initAll();
    openFromLocation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-bind when a section is (re)rendered in the theme editor.
  document.addEventListener('shopify:section:load', initAll);

  // Theme-editor preview: open the drawer while its section is selected.
  if (window.Shopify && window.Shopify.designMode) {
    var findBySection = function (sectionId) {
      return document.querySelector(SELECTOR + '[data-section-id="' + sectionId + '"]');
    };
    document.addEventListener('shopify:section:select', function (event) {
      var drawer = findBySection(event.detail.sectionId);
      if (drawer) {
        initDrawer(drawer);
        drawer.open = true;
      }
    });
    document.addEventListener('shopify:section:deselect', function (event) {
      var drawer = findBySection(event.detail.sectionId);
      if (drawer) drawer.open = false;
    });
  }
})();
